import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Avatar,
  Badge,
  Tooltip,
  Switch,
  FormControlLabel,
  Slider,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Radio,
  RadioGroup,
  FormGroup,
} from "@mui/material";
import {
  PhotoCamera,
  Add,
  Delete,
  Edit,
  Check,
  Close,
  Warning,
  Error,
  Info,
  LocationOn,
  Schedule,
  Image,
  Security,
  Engineering,
  Assignment,
  ExpandMore,
  Tune,
  Rule,
  Visibility,
  VisibilityOff,
  AspectRatio,
  HighQuality,
  Storage,
  Timer,
  Gps,
  CameraAlt,
  Straighten,
  ColorLens,
  Contrast,
  Brightness4,
  FormatSize,
  Speed,
  CheckCircle,
  ErrorOutline,
  CloudUpload,
} from "@mui/icons-material";

const PpPhotoRequirementsSystem = ({
  inspectionType = "general",
  permitType = "residential",
  onRequirementsUpdate,
  readOnly = false,
}) => {
  const [requirements, setRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Form state for requirement editing
  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    description: "",
    category: "",
    mandatory: true,
    quantity: { min: 1, max: 10 },
    qualityStandards: {
      minResolution: { width: 1920, height: 1080 },
      maxFileSize: 5 * 1024 * 1024, // 5MB
      minFileSize: 100 * 1024, // 100KB
      format: ["jpg", "jpeg", "png"],
      quality: 0.8,
      aspectRatio: "any", // 'any', '16:9', '4:3', '1:1'
      lighting: {
        minBrightness: 30,
        maxBrightness: 240,
        contrast: "medium",
      },
      technical: {
        blurThreshold: 0.1,
        noiseThreshold: 0.2,
        sharpnessMin: 0.3,
      },
    },
    locationRequirements: {
      gpsRequired: false,
      accuracyThreshold: 100, // meters
      specificLocations: [],
    },
    timingRequirements: {
      timeConstraints: "none", // 'none', 'business_hours', 'specific'
      validDateRange: { start: null, end: null },
      sequencing: "any", // 'any', 'before', 'after', 'during'
    },
    contentRequirements: {
      mustInclude: [],
      mustNotInclude: [],
      focusPoints: [],
      angles: [], // 'front', 'side', 'back', 'aerial', 'interior'
    },
    validationCriteria: {
      autoValidation: true,
      manualReview: false,
      aiValidation: false,
      checkpoints: [],
    },
  });

  // Mock requirements data
  const defaultRequirements = [
    {
      id: "REQ001",
      name: "Foundation Exterior Views",
      description: "Comprehensive exterior documentation of foundation work",
      category: "foundation",
      mandatory: true,
      quantity: { min: 4, max: 8 },
      qualityStandards: {
        minResolution: { width: 2048, height: 1536 },
        maxFileSize: 10 * 1024 * 1024,
        minFileSize: 200 * 1024,
        format: ["jpg", "jpeg"],
        quality: 0.9,
        aspectRatio: "4:3",
        lighting: {
          minBrightness: 50,
          maxBrightness: 200,
          contrast: "high",
        },
        technical: {
          blurThreshold: 0.05,
          noiseThreshold: 0.1,
          sharpnessMin: 0.5,
        },
      },
      locationRequirements: {
        gpsRequired: true,
        accuracyThreshold: 50,
        specificLocations: ["front", "back", "sides"],
      },
      timingRequirements: {
        timeConstraints: "business_hours",
        validDateRange: { start: null, end: null },
        sequencing: "any",
      },
      contentRequirements: {
        mustInclude: ["foundation", "grade_level", "drainage"],
        mustNotInclude: ["obstructions", "poor_lighting"],
        focusPoints: ["concrete_joints", "waterproofing", "backfill"],
        angles: ["front", "side", "back"],
      },
      validationCriteria: {
        autoValidation: true,
        manualReview: true,
        aiValidation: false,
        checkpoints: [
          "resolution_check",
          "lighting_check",
          "content_verification",
          "gps_validation",
        ],
      },
      status: "active",
      compliance: {
        total: 12,
        passed: 8,
        failed: 2,
        pending: 2,
      },
    },
    {
      id: "REQ002",
      name: "Electrical Panel Documentation",
      description: "Detailed electrical panel and connection photos",
      category: "electrical",
      mandatory: true,
      quantity: { min: 3, max: 6 },
      qualityStandards: {
        minResolution: { width: 1920, height: 1080 },
        maxFileSize: 8 * 1024 * 1024,
        minFileSize: 150 * 1024,
        format: ["jpg", "jpeg", "png"],
        quality: 0.85,
        aspectRatio: "any",
        lighting: {
          minBrightness: 60,
          maxBrightness: 220,
          contrast: "medium",
        },
        technical: {
          blurThreshold: 0.08,
          noiseThreshold: 0.15,
          sharpnessMin: 0.4,
        },
      },
      locationRequirements: {
        gpsRequired: true,
        accuracyThreshold: 25,
        specificLocations: ["electrical_room", "main_panel"],
      },
      timingRequirements: {
        timeConstraints: "none",
        validDateRange: { start: null, end: null },
        sequencing: "after",
      },
      contentRequirements: {
        mustInclude: ["panel_label", "breakers", "grounding"],
        mustNotInclude: ["panel_open", "unsafe_conditions"],
        focusPoints: [
          "service_entrance",
          "main_breaker",
          "grounding_electrode",
        ],
        angles: ["front", "interior"],
      },
      validationCriteria: {
        autoValidation: true,
        manualReview: false,
        aiValidation: true,
        checkpoints: [
          "resolution_check",
          "safety_compliance",
          "label_visibility",
          "code_compliance",
        ],
      },
      status: "active",
      compliance: {
        total: 18,
        passed: 15,
        failed: 1,
        pending: 2,
      },
    },
    {
      id: "REQ003",
      name: "Framing Connection Details",
      description: "Close-up shots of critical framing connections",
      category: "framing",
      mandatory: true,
      quantity: { min: 6, max: 12 },
      qualityStandards: {
        minResolution: { width: 2048, height: 1536 },
        maxFileSize: 12 * 1024 * 1024,
        minFileSize: 300 * 1024,
        format: ["jpg", "jpeg"],
        quality: 0.95,
        aspectRatio: "any",
        lighting: {
          minBrightness: 40,
          maxBrightness: 200,
          contrast: "high",
        },
        technical: {
          blurThreshold: 0.03,
          noiseThreshold: 0.08,
          sharpnessMin: 0.6,
        },
      },
      locationRequirements: {
        gpsRequired: false,
        accuracyThreshold: 100,
        specificLocations: ["beam_connections", "joist_hangers", "shear_walls"],
      },
      timingRequirements: {
        timeConstraints: "business_hours",
        validDateRange: { start: null, end: null },
        sequencing: "during",
      },
      contentRequirements: {
        mustInclude: ["fasteners", "member_sizes", "connections"],
        mustNotInclude: ["obstructed_view", "incomplete_work"],
        focusPoints: ["bolt_patterns", "weld_details", "hardware"],
        angles: ["front", "side"],
      },
      validationCriteria: {
        autoValidation: false,
        manualReview: true,
        aiValidation: true,
        checkpoints: [
          "connection_verification",
          "hardware_check",
          "code_compliance",
          "installation_quality",
        ],
      },
      status: "active",
      compliance: {
        total: 24,
        passed: 20,
        failed: 3,
        pending: 1,
      },
    },
  ];

  const photoCategories = [
    {
      value: "foundation",
      label: "Foundation",
      icon: <Engineering />,
      color: "primary",
    },
    {
      value: "framing",
      label: "Framing",
      icon: <Assignment />,
      color: "secondary",
    },
    {
      value: "electrical",
      label: "Electrical",
      icon: <Security />,
      color: "warning",
    },
    {
      value: "plumbing",
      label: "Plumbing",
      icon: <LocationOn />,
      color: "info",
    },
    {
      value: "mechanical",
      label: "Mechanical",
      icon: <Tune />,
      color: "success",
    },
    {
      value: "insulation",
      label: "Insulation",
      icon: <Info />,
      color: "default",
    },
    { value: "drywall", label: "Drywall", icon: <Image />, color: "default" },
    {
      value: "exterior",
      label: "Exterior",
      icon: <PhotoCamera />,
      color: "primary",
    },
    { value: "final", label: "Final", icon: <CheckCircle />, color: "success" },
  ];

  const qualityLevels = {
    low: {
      label: "Low",
      resolution: { width: 1280, height: 720 },
      quality: 0.6,
      fileSize: 2,
    },
    medium: {
      label: "Medium",
      resolution: { width: 1920, height: 1080 },
      quality: 0.8,
      fileSize: 5,
    },
    high: {
      label: "High",
      resolution: { width: 2048, height: 1536 },
      quality: 0.9,
      fileSize: 8,
    },
    premium: {
      label: "Premium",
      resolution: { width: 3840, height: 2160 },
      quality: 0.95,
      fileSize: 15,
    },
  };

  useEffect(() => {
    // Load requirements based on inspection and permit type
    setRequirements(defaultRequirements);
  }, [inspectionType, permitType]);

  const handleAddRequirement = () => {
    setEditForm({
      id: "",
      name: "",
      description: "",
      category: "",
      mandatory: true,
      quantity: { min: 1, max: 10 },
      qualityStandards: {
        minResolution: { width: 1920, height: 1080 },
        maxFileSize: 5 * 1024 * 1024,
        minFileSize: 100 * 1024,
        format: ["jpg", "jpeg", "png"],
        quality: 0.8,
        aspectRatio: "any",
        lighting: {
          minBrightness: 30,
          maxBrightness: 240,
          contrast: "medium",
        },
        technical: {
          blurThreshold: 0.1,
          noiseThreshold: 0.2,
          sharpnessMin: 0.3,
        },
      },
      locationRequirements: {
        gpsRequired: false,
        accuracyThreshold: 100,
        specificLocations: [],
      },
      timingRequirements: {
        timeConstraints: "none",
        validDateRange: { start: null, end: null },
        sequencing: "any",
      },
      contentRequirements: {
        mustInclude: [],
        mustNotInclude: [],
        focusPoints: [],
        angles: [],
      },
      validationCriteria: {
        autoValidation: true,
        manualReview: false,
        aiValidation: false,
        checkpoints: [],
      },
    });
    setEditDialogOpen(true);
  };

  const handleEditRequirement = (requirement) => {
    setEditForm(requirement);
    setEditDialogOpen(true);
  };

  const handleSaveRequirement = () => {
    if (editForm.id) {
      // Update existing
      setRequirements((prev) =>
        prev.map((req) =>
          req.id === editForm.id ? { ...editForm, status: "active" } : req
        )
      );
    } else {
      // Add new
      const newRequirement = {
        ...editForm,
        id: `REQ${Date.now()}`,
        status: "active",
        compliance: { total: 0, passed: 0, failed: 0, pending: 0 },
      };
      setRequirements((prev) => [...prev, newRequirement]);
    }

    setEditDialogOpen(false);

    // Notify parent component
    if (onRequirementsUpdate) {
      onRequirementsUpdate(requirements);
    }
  };

  const handleDeleteRequirement = (requirementId) => {
    if (window.confirm("Are you sure you want to delete this requirement?")) {
      setRequirements((prev) => prev.filter((req) => req.id !== requirementId));
    }
  };

  const validatePhotosAgainstRequirements = (photos, requirement) => {
    const validationResults = {
      passed: 0,
      failed: 0,
      issues: [],
    };

    photos.forEach((photo) => {
      let photoValid = true;
      let photoIssues = [];

      // Check quantity
      if (photos.length < requirement.quantity.min) {
        photoIssues.push(
          `Minimum ${requirement.quantity.min} photos required, only ${photos.length} provided`
        );
        photoValid = false;
      }

      // Check file size
      if (photo.size > requirement.qualityStandards.maxFileSize) {
        photoIssues.push(
          `File size too large: ${formatFileSize(photo.size)} > ${formatFileSize(requirement.qualityStandards.maxFileSize)}`
        );
        photoValid = false;
      }

      if (photo.size < requirement.qualityStandards.minFileSize) {
        photoIssues.push(
          `File size too small: ${formatFileSize(photo.size)} < ${formatFileSize(requirement.qualityStandards.minFileSize)}`
        );
        photoValid = false;
      }

      // Check resolution
      if (
        photo.width < requirement.qualityStandards.minResolution.width ||
        photo.height < requirement.qualityStandards.minResolution.height
      ) {
        photoIssues.push(
          `Resolution too low: ${photo.width}x${photo.height} < ${requirement.qualityStandards.minResolution.width}x${requirement.qualityStandards.minResolution.height}`
        );
        photoValid = false;
      }

      // Check GPS requirements
      if (requirement.locationRequirements.gpsRequired && !photo.gpsData) {
        photoIssues.push("GPS location required but not found");
        photoValid = false;
      }

      if (
        photo.gpsData &&
        photo.gpsData.accuracy >
          requirement.locationRequirements.accuracyThreshold
      ) {
        photoIssues.push(
          `GPS accuracy too low: ${photo.gpsData.accuracy}m > ${requirement.locationRequirements.accuracyThreshold}m`
        );
        photoValid = false;
      }

      // Check file format
      const fileExtension = photo.name.split(".").pop().toLowerCase();
      if (!requirement.qualityStandards.format.includes(fileExtension)) {
        photoIssues.push(
          `Invalid format: ${fileExtension}. Allowed: ${requirement.qualityStandards.format.join(", ")}`
        );
        photoValid = false;
      }

      if (photoValid) {
        validationResults.passed++;
      } else {
        validationResults.failed++;
        validationResults.issues.push({
          photoId: photo.id,
          issues: photoIssues,
        });
      }
    });

    return validationResults;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getRequirementStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "warning";
      case "disabled":
        return "default";
      default:
        return "default";
    }
  };

  const getComplianceColor = (compliance) => {
    const rate =
      compliance.total > 0 ? (compliance.passed / compliance.total) * 100 : 0;
    if (rate >= 90) return "success";
    if (rate >= 70) return "warning";
    return "error";
  };

  const renderRequirementCard = (requirement) => (
    <Card key={requirement.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor:
                  photoCategories.find((c) => c.value === requirement.category)
                    ?.color + ".main" || "grey.500",
                width: 32,
                height: 32,
              }}
            >
              {photoCategories.find((c) => c.value === requirement.category)
                ?.icon || <PhotoCamera />}
            </Avatar>
            <Box>
              <Typography variant="h6">{requirement.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {requirement.description}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={requirement.status}
              color={getRequirementStatusColor(requirement.status)}
              size="small"
            />
            {requirement.mandatory && (
              <Chip
                label="Required"
                color="error"
                size="small"
                variant="outlined"
              />
            )}
            {!readOnly && (
              <>
                <IconButton
                  size="small"
                  onClick={() => handleEditRequirement(requirement)}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteRequirement(requirement.id)}
                >
                  <Delete />
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <Typography variant="subtitle2">Requirements Details</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label={`${requirement.quantity.min}-${requirement.quantity.max} photos`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${formatFileSize(requirement.qualityStandards.maxFileSize)} max`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Quality Standards
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Resolution"
                      secondary={`${requirement.qualityStandards.minResolution.width} x ${requirement.qualityStandards.minResolution.height} minimum`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="File Size"
                      secondary={`${formatFileSize(requirement.qualityStandards.minFileSize)} - ${formatFileSize(requirement.qualityStandards.maxFileSize)}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Formats"
                      secondary={requirement.qualityStandards.format
                        .join(", ")
                        .toUpperCase()}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Quality"
                      secondary={`${Math.round(requirement.qualityStandards.quality * 100)}%`}
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Content Requirements
                </Typography>
                <List dense>
                  {requirement.contentRequirements.mustInclude.length > 0 && (
                    <ListItem>
                      <ListItemText
                        primary="Must Include"
                        secondary={requirement.contentRequirements.mustInclude.join(
                          ", "
                        )}
                      />
                    </ListItem>
                  )}
                  {requirement.contentRequirements.focusPoints.length > 0 && (
                    <ListItem>
                      <ListItemText
                        primary="Focus Points"
                        secondary={requirement.contentRequirements.focusPoints.join(
                          ", "
                        )}
                      />
                    </ListItem>
                  )}
                  {requirement.contentRequirements.angles.length > 0 && (
                    <ListItem>
                      <ListItemText
                        primary="Required Angles"
                        secondary={requirement.contentRequirements.angles.join(
                          ", "
                        )}
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>

              {requirement.locationRequirements.gpsRequired && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Location Requirements
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="GPS Required"
                        secondary={`±${requirement.locationRequirements.accuracyThreshold}m accuracy`}
                      />
                    </ListItem>
                    {requirement.locationRequirements.specificLocations.length >
                      0 && (
                      <ListItem>
                        <ListItemText
                          primary="Specific Locations"
                          secondary={requirement.locationRequirements.specificLocations.join(
                            ", "
                          )}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>
              )}

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Validation
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Auto Validation"
                      secondary={
                        requirement.validationCriteria.autoValidation
                          ? "Enabled"
                          : "Disabled"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Manual Review"
                      secondary={
                        requirement.validationCriteria.manualReview
                          ? "Required"
                          : "Optional"
                      }
                    />
                  </ListItem>
                  {requirement.validationCriteria.checkpoints.length > 0 && (
                    <ListItem>
                      <ListItemText
                        primary="Checkpoints"
                        secondary={requirement.validationCriteria.checkpoints.join(
                          ", "
                        )}
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Compliance Stats */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">Compliance Rate</Typography>
            <Typography variant="body2" color="text.secondary">
              {requirement.compliance.passed}/{requirement.compliance.total}{" "}
              photos passed
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={
              requirement.compliance.total > 0
                ? (requirement.compliance.passed /
                    requirement.compliance.total) *
                  100
                : 0
            }
            color={getComplianceColor(requirement.compliance)}
            sx={{ height: 8, borderRadius: 4, mb: 1 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label={`${requirement.compliance.passed} Passed`}
              color="success"
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${requirement.compliance.failed} Failed`}
              color="error"
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${requirement.compliance.pending} Pending`}
              color="warning"
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ ml: 1 }}>
          <Typography color="primary" variant="h4" mt={1} gutterBottom>
            Photo Requirements System
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {inspectionType} • {permitType} • {requirements.length} requirements
            defined
          </Typography>
        </Box>

        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddRequirement}
          >
            Add Requirement
          </Button>
        )}
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Rule color="primary" />
                <Box>
                  <Typography variant="h6">
                    {requirements.filter((r) => r.status === "active").length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Requirements
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Warning color="error" />
                <Box>
                  <Typography variant="h6">
                    {requirements.filter((r) => r.mandatory).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mandatory Photos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography variant="h6">
                    {Math.round(
                      requirements.reduce(
                        (acc, r) =>
                          acc +
                          (r.compliance.total > 0
                            ? (r.compliance.passed / r.compliance.total) * 100
                            : 0),
                        0
                      ) / requirements.length
                    )}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall Compliance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhotoCamera color="info" />
                <Box>
                  <Typography variant="h6">
                    {requirements.reduce((acc, r) => acc + r.quantity.min, 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Min Photos Required
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Requirements List */}
      {requirements.length === 0 ? (
        <Alert severity="info">
          No photo requirements defined yet. Click "Add Requirement" to get
          started.
        </Alert>
      ) : (
        <Box>
          {requirements.map((requirement) =>
            renderRequirementCard(requirement)
          )}
        </Box>
      )}

      {/* Edit Requirement Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editForm.id ? "Edit Requirement" : "Add New Requirement"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Requirement Name"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editForm.category}
                  label="Category"
                  onChange={(e) =>
                    setEditForm({ ...editForm, category: e.target.value })
                  }
                >
                  {photoCategories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {cat.icon}
                        {cat.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
                placeholder="Describe what photos are needed and their purpose..."
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.mandatory}
                    onChange={(e) =>
                      setEditForm({ ...editForm, mandatory: e.target.checked })
                    }
                  />
                }
                label="Mandatory Requirement"
              />
            </Grid>

            {/* Quantity Requirements */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Quantity Requirements
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Photos"
                value={editForm.quantity.min}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    quantity: {
                      ...editForm.quantity,
                      min: parseInt(e.target.value) || 1,
                    },
                  })
                }
                inputProps={{ min: 1, max: 50 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Photos"
                value={editForm.quantity.max}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    quantity: {
                      ...editForm.quantity,
                      max: parseInt(e.target.value) || 10,
                    },
                  })
                }
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>

            {/* Quality Standards */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Quality Standards
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Min Width (px)"
                type="number"
                value={editForm.qualityStandards.minResolution.width}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    qualityStandards: {
                      ...editForm.qualityStandards,
                      minResolution: {
                        ...editForm.qualityStandards.minResolution,
                        width: parseInt(e.target.value) || 1920,
                      },
                    },
                  })
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Min Height (px)"
                type="number"
                value={editForm.qualityStandards.minResolution.height}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    qualityStandards: {
                      ...editForm.qualityStandards,
                      minResolution: {
                        ...editForm.qualityStandards.minResolution,
                        height: parseInt(e.target.value) || 1080,
                      },
                    },
                  })
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography gutterBottom>
                Image Quality:{" "}
                {Math.round(editForm.qualityStandards.quality * 100)}%
              </Typography>
              <Slider
                value={editForm.qualityStandards.quality}
                onChange={(e, value) =>
                  setEditForm({
                    ...editForm,
                    qualityStandards: {
                      ...editForm.qualityStandards,
                      quality: value,
                    },
                  })
                }
                min={0.3}
                max={1.0}
                step={0.05}
                marks={[
                  { value: 0.3, label: "30%" },
                  { value: 0.6, label: "60%" },
                  { value: 0.8, label: "80%" },
                  { value: 1.0, label: "100%" },
                ]}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography gutterBottom>
                Max File Size:{" "}
                {formatFileSize(editForm.qualityStandards.maxFileSize)}
              </Typography>
              <Slider
                value={editForm.qualityStandards.maxFileSize}
                onChange={(e, value) =>
                  setEditForm({
                    ...editForm,
                    qualityStandards: {
                      ...editForm.qualityStandards,
                      maxFileSize: value,
                    },
                  })
                }
                min={1024 * 1024} // 1MB
                max={20 * 1024 * 1024} // 20MB
                step={1024 * 1024}
                marks={[
                  { value: 1024 * 1024, label: "1MB" },
                  { value: 5 * 1024 * 1024, label: "5MB" },
                  { value: 10 * 1024 * 1024, label: "10MB" },
                  { value: 20 * 1024 * 1024, label: "20MB" },
                ]}
              />
            </Grid>

            {/* Location Requirements */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Location Requirements
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.locationRequirements.gpsRequired}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        locationRequirements: {
                          ...editForm.locationRequirements,
                          gpsRequired: e.target.checked,
                        },
                      })
                    }
                  />
                }
                label="GPS Location Required"
              />
            </Grid>

            {editForm.locationRequirements.gpsRequired && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="GPS Accuracy Threshold (meters)"
                  type="number"
                  value={editForm.locationRequirements.accuracyThreshold}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      locationRequirements: {
                        ...editForm.locationRequirements,
                        accuracyThreshold: parseInt(e.target.value) || 100,
                      },
                    })
                  }
                  inputProps={{ min: 1, max: 1000 }}
                />
              </Grid>
            )}

            {/* Validation Criteria */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Validation Criteria
              </Typography>
            </Grid>

            <Grid size={12}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.validationCriteria.autoValidation}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          validationCriteria: {
                            ...editForm.validationCriteria,
                            autoValidation: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Auto Validation"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.validationCriteria.manualReview}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          validationCriteria: {
                            ...editForm.validationCriteria,
                            manualReview: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Manual Review Required"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editForm.validationCriteria.aiValidation}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          validationCriteria: {
                            ...editForm.validationCriteria,
                            aiValidation: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="AI Validation"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveRequirement}
            disabled={!editForm.name || !editForm.category}
          >
            {editForm.id ? "Update" : "Create"} Requirement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpPhotoRequirementsSystem;
